// nuolino-admin/src/lib/format.ts
// Formatadores centralizados. PT-BR por padrão; en-US quando locale='en-US'.
// Componentes consomem daqui — não usam `Intl` espalhado.

import { format as fnsFormat, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { ptBR, enUS } from 'date-fns/locale';

export const SUPPORTED_LOCALES = ['pt-BR', 'en-US'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = 'pt-BR';

const dateLocales = { 'pt-BR': ptBR, 'en-US': enUS } as const;
const timeZone = 'America/Sao_Paulo'; // Default BR. F-3.6 (opção) deixa configurável.

function resolveLocale(locale: SupportedLocale): typeof ptBR {
  return dateLocales[locale];
}

function isValidISODate(input: string): boolean {
  return /^\d{4}-\d{2}-\d{2}/.test(input);
}

export function formatDate(input: string | Date | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? (isValidISODate(input) ? parseISO(input) : new Date(input)) : input;
  if (Number.isNaN(date.getTime())) return '—';
  return fnsFormat(date, locale === 'pt-BR' ? "dd 'de' MMMM 'de' yyyy" : 'MMMM dd, yyyy', { locale: resolveLocale(locale) });
}

export function formatShortDate(input: string | Date | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? (isValidISODate(input) ? parseISO(input) : new Date(input)) : input;
  if (Number.isNaN(date.getTime())) return '—';
  return fnsFormat(date, locale === 'pt-BR' ? 'dd/MM/yyyy' : 'MM/dd/yyyy', { locale: resolveLocale(locale) });
}

export function formatTime(input: string | Date | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? (isValidISODate(input) ? parseISO(input) : new Date(input)) : input;
  if (Number.isNaN(date.getTime())) return '—';
  return formatInTimeZone(date, timeZone, locale === 'pt-BR' ? 'HH:mm' : 'h:mm a');
}

export function formatDateTime(input: string | Date | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE): string {
  if (!input) return '—';
  return `${formatShortDate(input, locale)} · ${formatTime(input, locale)}`;
}

export function formatRelativeFromNow(input: string | Date | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE): string {
  if (!input) return '—';
  const date = typeof input === 'string' ? (isValidISODate(input) ? parseISO(input) : new Date(input)) : input;
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = date.getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  const minutes = Math.round(absMs / 60_000);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);
  const future = diffMs > 0;
  if (locale === 'pt-BR') {
    if (minutes < 1) return future ? 'agora' : 'agora';
    if (minutes < 60) return future ? `em ${minutes} min` : `há ${minutes} min`;
    if (hours < 24) return future ? `em ${hours} h` : `há ${hours} h`;
    return future ? `em ${days} d` : `há ${days} d`;
  }
  if (minutes < 1) return 'now';
  if (minutes < 60) return future ? `in ${minutes} min` : `${minutes} min ago`;
  if (hours < 24) return future ? `in ${hours} h` : `${hours} h ago`;
  return future ? `in ${days} d` : `${days} d ago`;
}

export function formatCurrency(value: number | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE, currency = 'BRL'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
}

export function formatNumber(value: number | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE, fractionDigits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).format(value);
}

export function formatPercent(value: number | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE, fractionDigits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).format(value);
}

export function formatWeightKg(value: number | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${formatNumber(value, locale, 1)} kg`;
}

export function formatHeightCm(value: number | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${formatNumber(value, locale, 1)} cm`;
}
